/* Inline inspiration videos. No player SDK, fullscreen, audio or cart dependencies. */
(function () {
  'use strict';
  if (window.CertiClearPoolsideLoaded) return;
  window.CertiClearPoolsideLoaded = true;
  const instances = new Map();

  function mount(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('[data-video-track]');
    const toggle = root.querySelector('[data-video-toggle]');
    const previous = root.querySelector('[data-video-prev]');
    const next = root.querySelector('[data-video-next]');
    const status = root.querySelector('[data-video-status]');
    const videos = Array.from(root.querySelectorAll('[data-poolside-video]'));
    if (!track || !toggle || !videos.length) return;
    const media = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const visible = new Set();
    const failed = new Set();
    const pending = new Set();
    const interrupted = new Set();
    const cleanup = [];
    let paused = Boolean(media?.matches || window.navigator?.connection?.saveData);
    let rotationPaused = false;
    let hovering = false;
    let destroyed = false;
    let timer = null;
    let observer;

    function listen(node, event, callback, options) {
      if (!node) return;
      node.addEventListener(event, callback, options);
      cleanup.push(() => node.removeEventListener(event, callback, options));
    }
    function clearTimer() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    }
    function wantsPlayback(video) {
      return !destroyed && !paused && !document.hidden && visible.has(video) && !failed.has(video);
    }
    function advance(direction, automatic) {
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      if (maximum < 2) return;
      const first = videos[0].parentElement;
      const second = videos[1]?.parentElement;
      const step = second ? second.getBoundingClientRect().left - first.getBoundingClientRect().left : first.getBoundingClientRect().width;
      let left = track.scrollLeft + direction * Math.abs(step);
      if (direction > 0 && track.scrollLeft >= maximum - 2) left = 0;
      if (direction < 0 && track.scrollLeft <= 2) left = maximum;
      track.scrollTo({ left: Math.max(0, Math.min(maximum, left)), behavior: media?.matches || (automatic && left === 0) ? 'auto' : 'smooth' });
    }
    function schedule() {
      clearTimer();
      if (destroyed || paused || rotationPaused || hovering || media?.matches || document.hidden || !visible.size || videos.length < 2) return;
      timer = window.setTimeout(() => {
        timer = null;
        advance(1, true);
        schedule();
      }, 6500);
    }
    function sync() {
      if (destroyed) return;
      toggle.textContent = paused ? 'Play videos' : 'Pause motion';
      root.setAttribute('data-motion', paused ? 'paused' : 'playing');
      videos.forEach(video => {
        if (!wantsPlayback(video)) {
          if (pending.has(video)) interrupted.add(video);
          video.pause();
          return;
        }
        if (!video.getAttribute('src')) {
          video.src = video.getAttribute('data-src');
          video.load();
        }
        video.muted = true;
        video.defaultMuted = true;
        video.volume = 0;
        video.playsInline = true;
        if (!video.paused || pending.has(video)) return;
        pending.add(video);
        let attempt;
        try { attempt = video.play(); } catch (error) { attempt = Promise.reject(error); }
        Promise.resolve(attempt).then(() => {
          if (!wantsPlayback(video)) video.pause();
        }).catch(error => {
          if (!wantsPlayback(video) || error?.name === 'AbortError') return;
          // An autoplay-policy failure is not a network retry loop.
          paused = true;
          status.textContent = 'Playback is paused. Select Play videos to try again.';
          sync();
        }).finally(() => {
          pending.delete(video);
          const retry = interrupted.delete(video);
          // A rapid exit/re-entry can abort an earlier play while the new intent
          // waits for it. Retry that transition once, never a policy failure.
          if (retry && wantsPlayback(video)) sync();
        });
      });
      schedule();
    }
    function stopRotation() {
      rotationPaused = true;
      clearTimer();
    }
    videos.forEach(video => {
      video.muted = true;
      video.defaultMuted = true;
      video.volume = 0;
      listen(video, 'playing', () => {
        if (!wantsPlayback(video)) { video.pause(); return; }
        video.parentElement.setAttribute('data-video-ready', '');
      });
      listen(video, 'error', () => {
        failed.add(video);
        video.pause();
        video.parentElement.removeAttribute('data-video-ready');
        const notice = video.parentElement.querySelector('[data-video-error]');
        if (notice) notice.hidden = false;
      });
    });
    listen(toggle, 'click', () => {
      paused = !paused;
      if (!paused) rotationPaused = false;
      status.textContent = '';
      sync();
    });
    listen(previous, 'click', () => { stopRotation(); advance(-1, false); });
    listen(next, 'click', () => { stopRotation(); advance(1, false); });
    listen(root, 'focusin', stopRotation);
    listen(root, 'mouseenter', () => { hovering = true; clearTimer(); });
    listen(root, 'mouseleave', () => { hovering = false; schedule(); });
    listen(track, 'pointerdown', stopRotation, { passive: true });
    listen(track, 'wheel', stopRotation, { passive: true });
    listen(track, 'keydown', event => {
      stopRotation();
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        advance(event.key === 'ArrowRight' ? 1 : -1, false);
      }
    });
    listen(document, 'visibilitychange', sync);
    const preferenceChanged = () => {
      if (media.matches) paused = true;
      sync();
    };
    if (media?.addEventListener) listen(media, 'change', preferenceChanged);
    else if (media?.addListener) {
      media.addListener(preferenceChanged);
      cleanup.push(() => media.removeListener(preferenceChanged));
    }
    if (window.IntersectionObserver) {
      observer = new window.IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target.querySelector('[data-poolside-video]');
          if (entry.isIntersecting && entry.intersectionRatio >= .25) visible.add(video);
          else visible.delete(video);
        });
        sync();
      }, { threshold: [0, .25] });
      videos.forEach(video => observer.observe(video.parentElement));
    } else {
      // Older browsers: viewport checks, still no eager loading of all clips.
      const checkVisibility = () => {
        const bounds = track.getBoundingClientRect();
        videos.forEach(video => {
          const box = video.parentElement.getBoundingClientRect();
          const width = Math.min(box.right, bounds.right, window.innerWidth) - Math.max(box.left, bounds.left, 0);
          const height = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
          if (width > box.width * .25 && height > box.height * .25) visible.add(video);
          else visible.delete(video);
        });
        sync();
      };
      listen(window, 'scroll', checkVisibility, { passive: true });
      listen(window, 'resize', checkVisibility, { passive: true });
      listen(track, 'scroll', checkVisibility, { passive: true });
      checkVisibility();
    }
    root.querySelector('[data-video-controls]').hidden = false;
    instances.set(root, () => {
      destroyed = true;
      clearTimer();
      observer?.disconnect();
      cleanup.forEach(dispose => dispose());
      videos.forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); });
      visible.clear();
      instances.delete(root);
    });
    sync();
  }
  function init(container) {
    if (container.matches?.('[data-video-carousel]')) mount(container);
    container.querySelectorAll('[data-video-carousel]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => {
      if (event.target === root || event.target.contains(root)) dispose();
    });
  });
})();
